import axios from 'axios'
import uuRequestKeys from 'uuid/v1'
import { webRequestClient } from '@/services'
import helpers from './helpers'
import { errorHandler } from '@/utils/helpers'
import { includes, head, get, size, isNil, isEmpty } from 'lodash'
import { BUILDER_CRITERIA_LIMITS } from '@/utils/constants.js'

//cancel token set when volumes request is called
let source = { cancel: null, params: null }

//function to cancel previous request if needed
function cancelRequest(cs, requestParams) {
    if (!isNil(cs) && !isNil(cs.params)) {
        if (cs.params.method === requestParams.method) cs.cancel.cancel('CANCELED_REQUEST')
    }
}

const actions = {
    addCriterionInBox({ commit, dispatch, state, getters }, params) {
        const jsonKeySubType = params.criterion.jsonKey.subType
        const jsonKeyType = params.criterion.jsonKey.type
        const category =
            jsonKeyType === 'SecondPartyCluster'
                ? 'secondParty'
                : helpers.category({
                    type: jsonKeyType,
                    subType: jsonKeySubType
                })
        const criteriaList = []
        const criteria =
            params.nodeType === 'children'
                ? getters.getDescendants({
                    category: category,
                    level: 1,
                    node: params.criterion
                })
                : [params.criterion]
        let ancestorsJsonKeyNames = []

        // Stop process if the formula becomes too big and throw warning
        if (helpers.getFormulaSize(state.formula) + criteria.length > BUILDER_CRITERIA_LIMITS) {
            commit(
                'notifications/setAddedNotification',
                {
                    type: 'warning',
                    message: `The formula can only support up to ${BUILDER_CRITERIA_LIMITS} criterias`
                },
                { root: true }
            )
            return
        }

        for (const [index, criterion] of criteria.entries()) {
            const recency = params.criterion.recency
                ? params.criterion.recency
                : helpers.setRecency(criterion, getters.getFirstPartyInFormula(), state.formulaSettings.settings.recency)
            const criterionFormula = helpers.buildCriterionForFormula({
                ...criterion,
                criterionSubType: params.criterionSubType,
                formulaSettings: state.formulaSettings,
                recency,
                range: params.criterion.range,
                level: params.criterion.level
            })

            //add first criterion
            if (
                state.focusedBox.index === 0 &&
                state.focusedBox.type === 'inclusion' &&
                head(state.formula.inclusion) &&
                size(head(state.formula.inclusion).criteria) === 0
            ) {
                commit('ui/builder/setCriterionInBox', { criterion: criterionFormula }, { root: true })
                //add criterion(a)
            } else {
                //check if criterion is already in formula
                if (
                    getters.getCriterionAlreadyInBox({
                        criterionName: criterionFormula.criterion.name,
                        focusedBox: state.focusedBox
                    })
                ) {
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'warning',
                            message: criterion.label + ' already added'
                        },
                        { root: true }
                    )
                } else {
                    const boxData = getters.getJsonKeyNamesInBox({
                        focusedBox: state.focusedBox
                    })
                    if (index === 0) {
                        ancestorsJsonKeyNames = getters.getAncestorsJsonKeyNames({
                            node: criterion,
                            ancestors: [],
                            level: Infinity,
                            category: category
                        })
                    }

                    //Georadar points don't have hierarchies
                    if (criterion.jsonKey.type !== 'GeoradarID')
                        dispatch('removeAncestorsAndDescendantsFromFormula', {
                            criterion,
                            category,
                            boxData,
                            ancestors: ancestorsJsonKeyNames
                        })

                    // Add criterion
                    criteriaList.push(criterionFormula)
                    //commit('ui/builder/setCriterionInBox', { criterion: criterionFormula }, { root: true })
                }
            }
        }
        dispatch('fillBox', { criteriaList })
    },

    fillBox({ commit }, params) {
        if (!isEmpty(params.criteriaList)) {
            if (size(params.criteriaList) === 1) {
                commit('setCriterionInBox', {
                    criterion: head(params.criteriaList)
                })
            } else {
                commit('setCriteriaInBox', { criteria: params.criteriaList })
            }
        }
    },

    removeAncestorsAndDescendantsFromFormula({ commit, state, getters }, params) {
        for (const jsonKeyNameInBox of params.boxData) {
            // List of ancestors criterion names (recursive)
            // List of descendants criterion names (recursive)
            let descendantsJsonKeyNames = []
            descendantsJsonKeyNames = getters.getDescendantsJsonKeyNames({
                node: params.criterion,
                level: Infinity,
                category: params.category
            })
            // Child replace his parent OR parent replace his children
            if (includes(params.ancestors, jsonKeyNameInBox) || includes(descendantsJsonKeyNames, jsonKeyNameInBox)) {
                commit(
                    'ui/builder/setDeletedCriterion',
                    {
                        name: jsonKeyNameInBox,
                        boxType: state.focusedBox.type,
                        boxIndex: state.focusedBox.index
                    },
                    { root: true }
                )
            }
        }
    },

    disableFormulaBox({ commit }, params) {
        commit('setDisabledFormulaBox', {
            boxType: params.boxType,
            boxIndex: params.boxIndex,
            isDisabled: null
        })
        commit('setDisabledCriteriaInBox', {
            boxType: params.boxType,
            boxIndex: params.boxIndex
        })
    },

    retrieveFormulaMetrics({ dispatch, state }, params) {
        const v1Formula = params.v1Formula
        const v2Formula = params.v2Formula
        const activeTab = params.activeTab

        return dispatch('processStatistics', {
            formula: v2Formula,
            type: 'volumes'
        }).then(() => {
            if (activeTab === 'breakdown') {
                const totalVolume = get(state, 'metrics.volumes.total_desktop', null)
                if (totalVolume !== 0) {
                    dispatch('processStatistics', {
                        formula: v1Formula,
                        type: 'breakdown'
                    })
                }
            } else if (activeTab === 'summary') {
                dispatch('processStatistics', {
                    formula: v1Formula,
                    type: 'summary'
                })
            }
        })
    },

    async processStatistics({ commit, state, rootState }, params) {
        const type = params.type
        const formula = !isEmpty(params.formula) ? params.formula : { recency: state.formulaSettings.settings.recency }
        const account_id = get(rootState, 'account.accountId', '')
        // if version is in params, call is from monitoring page to get generic_formula volumes
        // => to disable or not bootstrap button in Details section
        const isMonitoringRequest = !isNil(get(params, 'version', null))
        let version
        if (isMonitoringRequest) version = params.version
        else version = type === 'volumes' ? 2 : 1
        const requestParams = {
            formula,
            account_id,
            method: type === 'volumes' ? 'count' : type,
            version
        }

        try {
            commit('setIsLoading', { item: type, isLoading: true })
            const requestKey = uuRequestKeys()

            commit('setLastUuRequestKey', { item: type, key: requestKey })

            let response = { status: 'NOT_READY' }
            let tryCounter = 0
            const maxTries = 30
            while (response.status && response.status === 'NOT_READY' && requestKey === state.lastUuRequestKey[type] && tryCounter < maxTries) {
                tryCounter++
                //cancel previous request
                cancelRequest(source, requestParams)

                //set cancelToken
                source = {
                    cancel: axios.CancelToken.source(),
                    params: requestParams
                }
                response = await webRequestClient.post('/audience_admin/criteria/statistic.json', requestParams, {
                    cancelToken: source.cancel.token
                })
                const canceledRequest = get(response, 'status', '') === 'CANCELED_REQUEST'
                if (get(response, 'status', '') === 'KO' || get(response, 'status', '') === 401) {
                    //no response or functionnal error
                    commit('ui/monitoring/setSelectedAudienceHaveDeletedCriteria', { result: true }, { root: true })
                    helpers.processStatisticsErrorHandler(`Unable to retrieve ${type}`, type, commit)
                } else if (!canceledRequest) {
                    const isStillPending = get(response, 'status', '') === 'NOT_READY'
                    //if request is completed, add to store
                    //otherwise, loop starts again
                    if (!isStillPending) {
                        if (type === 'volumes') {
                            if (params.from === 'cclone') {
                                commit('ui/cclone/setFormulaVolumes', { volumes: response }, { root: true })
                            } else if (isMonitoringRequest) {
                                commit(
                                    'ui/monitoring/setFormulaVolumes',
                                    {
                                        volumes: response.total_doc,
                                        audienceId: params.audienceId
                                    },
                                    { root: true }
                                )
                            } else {
                                const _volumes = isEmpty(response) ? response : response.formula.volume
                                //add total volumes to ui/builder store
                                commit('setVolumes', { volumes: _volumes })
                                if (response.formula.children) {
                                    //add volumes for each segment
                                    commit('setFormulaVolumes', {
                                        volumes: response.formula.children
                                    })
                                }
                            }
                        } else if (type === 'summary') commit('setSummary', { summary: response })
                        else if (type === 'breakdown') commit('setBreakdown', { breakdown: response })
                        commit('ui/monitoring/setSelectedAudienceHaveDeletedCriteria', { result: false }, { root: true })
                        commit('setIsLoading', {
                            item: type,
                            isLoading: false
                        })
                    } else {
                        await new Promise(resolve => {
                            setTimeout(() => resolve(), 1000)
                        })
                    }
                }
            }
            if (get(response, 'status', '') === 'NOT_READY' && tryCounter === maxTries) {
                helpers.processStatisticsErrorHandler(`Unable to retrieve ${type}`, type, commit)
            }
        } catch (error) {
            commit('setIsLoading', { item: type, isLoading: false })
            if (type === 'volumes')
                commit(
                    'ui/cclone/setFormulaVolumes',
                    {
                        volumes: {
                            error: true
                        }
                    },
                    { root: true }
                )
            commit('ui/monitoring/setSelectedAudienceHaveDeletedCriteria', { result: true }, { root: true })
            if (!(isMonitoringRequest && type === 'volumes')) helpers.processStatisticsErrorHandler(error, type, commit)
        }
    },

    requestAccountTags({ _ }, params) {
        const _params = {
            params: {
                conditions: {
                    label: { '-like': '%' + params + '%' }
                },
                list_limit: 0
            }
        }
        return webRequestClient
            .get('/audience_admin/tags.json', _params)
            .then(response => {
                if (response && !isNil(get(response, 'list', null))) {
                    return response.list.filter(item => {
                        if (item.label.includes(params)) {
                            return item.label
                        }
                    })
                }
            })
            .catch(error => {
                errorHandler(error)
            })
    },

    closeAudienceForm({ commit }) {
        commit('setActiveTabFormAudience', { tab: 'audienceDetails' })
        // clear Volume Alert
        commit('ui/volumeAlert/setDefaultVolumeAlertForm', {}, { root: true })
        // clear transfers
        commit('ui/transfers/setDefaultTransferForm', {}, { root: true })
        // Close form
        commit('setDisplayFormAudience', { display: false, mode: null })
        commit('setActionFormAudience', { action: 'create' })
    },

    createAudienceForm({ commit }) {
        // close warning modal
        commit('setDisplayModal', { item: 'warning', display: false })

        // Display create audience form
        commit('setDisplayFormAudience', { display: true, mode: 'audience' })
    },

    openEditAudienceForm({ commit }) {
        // Display edit audience form
        commit('setDisplayFormAudience', {
            display: true,
            mode: 'audience',
            action: 'edit'
        })
    }
}

export default actions
